import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import DOMPurify from "dompurify";
import parse, { domToReact } from "html-react-parser";

// Renders CMS article HTML in the existing article design (prose-dad), with site links, responsive tables and CTA blocks.
const prepare = (html) => {
  const clean = DOMPurify.sanitize(String(html || ""), { ADD_ATTR: ["target", "data-cta", "data-heading", "data-text", "data-label", "data-href"] });
  const doc = new DOMParser().parseFromString(`<div>${clean}</div>`, "text/html");
  const root = doc.body.firstChild;
  root.querySelectorAll("li > p:only-child").forEach((p) => p.replaceWith(...p.childNodes));
  root.querySelectorAll("table").forEach((t) => {
    t.querySelectorAll("colgroup").forEach((c) => c.remove());
    const rows = [...t.querySelectorAll("tr")];
    const first = rows[0];
    if (first && [...first.children].every((c) => c.tagName === "TH")) {
      const thead = doc.createElement("thead");
      thead.appendChild(first);
      t.insertBefore(thead, t.firstChild);
      first.querySelectorAll("th").forEach((th) => { th.setAttribute("scope", "col"); th.querySelectorAll("p").forEach((p) => p.replaceWith(...p.childNodes)); });
    }
    const labels = first ? [...first.children].map((c) => c.textContent.trim()) : [];
    t.querySelectorAll("tbody tr").forEach((tr) => [...tr.children].forEach((td, i) => {
      td.setAttribute("data-label", labels[i] || "");
      td.querySelectorAll("p").forEach((p) => p.replaceWith(...p.childNodes));
    }));
    t.removeAttribute("style");
    const wrap = doc.createElement("div");
    wrap.className = "table-wrap";
    t.replaceWith(wrap);
    wrap.appendChild(t);
  });
  return root.innerHTML;
};

export const CtaBox = ({ heading, text, label, href, testid = "blog-body-cta" }) => {
  const to = href || "/contact";
  const cls = "btn-accent";
  return (
    <div className="not-prose my-10 rounded-[28px] bg-[#13052A] p-8 text-white sm:p-10" data-testid={testid}>
      {heading && <div className="text-2xl font-extrabold text-white">{heading}</div>}
      {text && <div className="mt-3 text-base text-violet-100">{text}</div>}
      {label && (
        <div className="mt-6">
          {to.startsWith("/")
            ? <Link to={to} className={cls}>{label} <ArrowUpRight className="h-4 w-4" /></Link>
            : <a href={to} target="_blank" rel="noopener noreferrer" className={cls}>{label} <ArrowUpRight className="h-4 w-4" /></a>}
        </div>
      )}
    </div>
  );
};

const options = {
  replace(node) {
    if (node.type !== "tag") return undefined;
    if (node.name === "div" && node.attribs?.["data-cta"] !== undefined) {
      const a = node.attribs;
      return <CtaBox heading={a["data-heading"]} text={a["data-text"]} label={a["data-label"]} href={a["data-href"]} />;
    }
    if (node.name === "a" && node.attribs?.href?.startsWith("/") && !node.attribs.href.startsWith("/api/")) {
      return <Link to={node.attribs.href}>{domToReact(node.children, options)}</Link>;
    }
    if (node.name === "img") {
      const { src, alt = "", title } = node.attribs;
      return <img src={src} alt={alt} title={title} loading="lazy" decoding="async" className="my-8 h-auto w-full rounded-[20px]" />;
    }
    return undefined;
  },
};

export const PostBody = ({ html }) => <div className="prose-dad" data-testid="blog-article-body">{parse(prepare(html), options)}</div>;
